/**
 * Lo que se escoge y se escribe en «Configuración», la del negocio entero.
 *
 * Apart from the screen so the rules for reading what the owner types can be tested, because the
 * mistakes they catch are the expensive kind: a dollar rate typed without its point — 3650 for
 * 36.50 — turns every dollar handed over the counter into a hundred of them.
 */

import { FX_SCALE, parseFxRate, type FxRate, type SettingKey } from '@abastece/shared';
import type { Clave } from '../i18n/index.js';

/** Coin steps a cash total can be rounded to, in centavos. 1 is no rounding at all. */
export const PASOS_REDONDEO = [1, 25, 50, 100] as const;

/** Minutes without a touch before the PIN pad comes back. */
export const MINUTOS_BLOQUEO = [1, 2, 5, 10, 15, 30, 60] as const;

/** Days before its date that a lot starts showing as about to expire. */
export const DIAS_VENCIMIENTO = [7, 15, 30, 60, 90] as const;

/** Hours without reaching the server before a device shows on the owner's home screen. */
/** In whole days, a day at the least (2026-10-13, `horasDeAviso`): a phone off for the night has not stopped syncing. */
export const HORAS_EQUIPO_CALLADO = [24, 48, 72, 168] as const;

/**
 * Córdobas to the dollar that a typed rate has to fall between. The córdoba has been between 20
 * and 37 to the dollar for fifteen years; this is a guard against a missing point, not a forecast.
 */
export const TASA_MINIMA = 10;
export const TASA_MAXIMA = 100;

/** A margin is a share of the price, so a floor near 100% is a price with no cost in it at all. */
export const MARGEN_MAXIMO = 90;

export type Lectura<T> = { ok: true; valor: T } | { ok: false; error: 'formato' | 'rango' };

/** A rate as typed, «36.50», into what the settings keep. */
export function leerTasa(texto: string): Lectura<FxRate> {
  const tasa = parseFxRate(texto);
  if (tasa === null) return { ok: false, error: 'formato' };
  if (tasa < TASA_MINIMA * FX_SCALE || tasa > TASA_MAXIMA * FX_SCALE) return { ok: false, error: 'rango' };
  return { ok: true, valor: tasa };
}

/** A percentage as typed, «12.5» or «12,5 %», into basis points. */
export function leerMargen(texto: string): Lectura<number> {
  const limpio = texto.trim().replace(/\s*%$/, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(limpio)) return { ok: false, error: 'formato' };
  const bp = Math.round(Number(limpio) * 100);
  return bp > MARGEN_MAXIMO * 100 ? { ok: false, error: 'rango' } : { ok: true, valor: bp };
}

/** Basis points as the owner reads them: 1000 is «10%», 1250 «12.5%». */
export const verMargen = (bp: number): string => `${bp / 100}%`;

/**
 * The usual choices, and whatever is set now among them even when it is none of those — or the
 * control would show a value that is not the one in force.
 */
export function conActual(opciones: readonly number[], actual: number): number[] {
  return opciones.includes(actual) ? [...opciones] : [...opciones, actual].sort((a, b) => a - b);
}

/**
 * Each setting by the name it goes by on screen, for the bitácora to say which one changed. A
 * setting with no screen keeps the bare «Configuración cambiada», but for the trucks: their screen
 * went with «Reparto» (2026-10-04), and the trucks registered before are still in the log.
 */
export const NOMBRE_AJUSTE: Partial<Record<SettingKey, Clave>> = {
  negocioNombre: 'negocio.nombre',
  negocioDireccion: 'negocio.direccion',
  negocioTelefono: 'negocio.telefono',
  negocioRuc: 'negocio.ruc',
  reciboPie: 'negocio.pie',
  fxTasaTienda: 'negocio.tasaTienda',
  fxTasaOficial: 'negocio.tasaOficial',
  cajaRedondeoCentavos: 'negocio.redondeo',
  cajaConteoCiego: 'negocio.conteoCiego',
  cajaFondo: 'negocio.fondo',
  precioMargenMinimoBp: 'negocio.margenMinimo',
  ventaRapidaMaxima: 'negocio.ventaRapida',
  faltanteAviso: 'negocio.faltanteAviso',
  cajaReconteoDesde: 'negocio.reconteo',
  bloqueoMinutos: 'negocio.bloqueoNombre',
  anularConDueno: 'negocio.anularConDueno',
  apartarPedidos: 'negocio.apartarPedidos',
  venderSoloLoQueHay: 'negocio.venderSoloLoQueHay',
  equipoCalladoHoras: 'negocio.equipoCallado',
  vencimientoAlertaDias: 'negocio.vencimientoAlerta',
  camiones: 'ajustes.camiones',
  gastosFijos: 'fijos.titulo',
  metaVentasMes: 'analisis.metas.ventas',
  metaUtilidadMes: 'analisis.metas.utilidad',
  presupuestosGastos: 'analisis.presupuesto.titulo',
};
